import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  resource,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { catchError, firstValueFrom, of, Subject, takeUntil } from 'rxjs';
import {
  EventqAccentCheckbox,
  EventqActivateTalkdownPopover,
  EventqButton,
  EventqCameraGrid,
  EventqCameraListPanel,
  EventqCameraPages,
  EventqCloseEventPopover,
  EventqCollapsibleSearchPanel,
  EventqCollapsibleSearchPanelBody,
  EventqDataTable,
  EventqDataTableColumn,
  EventqDataTableRow,
  EventqDatetimePicker,
  EventqDialogPopover,
  EventqDisarmSitePopover,
  EventqEventMetaBar,
  EventqLinedTab,
  EventqParkEventPopover,
  EventqPopoverSearch,
  EventqRaiseEventPopover,
  EventqSelect,
  EventqShortcutScope,
  EventqSortState,
  EventqTalkdownGroup,
  EventqToastService,
  EventqToggleTab,
  EventqViewAlarmPopover,
  EventqActionLoader,
  PrefixUrlPipe,
  createDefaultSiteDisarmUntilAt,
  resolveDisplayedDisarmFixedDurationMinutes,
  normalizeCameraGridLayout,
  type DisarmSiteConfirm,
  type EventqCameraGridLayout,
  type EventqSelectOption,
  type SiteDisarmFixedDurationMinutes,
  type SiteDisarmForOption,
  type ActivateTalkdownConfirm,
  type ActivateTalkdownOption,
  ACTIVATE_TALKDOWN_OPTIONS,
} from '@ecam/eventq-components';
import { AGENT_SHORTCUT_ACTION_IDS } from '../../../../core/shortcuts/agent-shortcuts';
import type {
  EventOutcomeConfirm,
  EventOutcomePriority,
  ParkEventConfirm,
  ParkEventDurationMinutes,
} from '@ecam/eventq-components';
import {
  AlarmsFooterTab,
  EventProcessingMode,
  EventViewPopoverType,
  PlaybackViewMode,
  createAlarmsClipModeReset,
  type SiteDisarmForType,
} from '../../../../core/models/browser-tab.models';
import {
  EventAlarmRow,
  EventAiTagVariant,
  EventAlarmsResponse,
  EventCameraItem,
  EventCameraTimeZone,
  EventCamerasResponse,
  EventDetail,
} from '../../../../core/models/entities/event-detail.model';
import { EventOutcomesResponse } from '../../../../core/models/entities/event-outcomes.model';
import { SiteDisarmReasonsResponse } from '../../../../core/models/entities/site-disarm-reasons.model';
import { AgentAccessService } from '../../../../core/services/agent-access.service';
import { AgentDataService } from '../../../../core/services/agent-data.service';
import { AlarmViewedStorageService } from '../../../../core/services/alarm-viewed-storage.service';
import { BrowserTabNavigationService } from '../../../../core/services/browser-tab-navigation.service';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import {
  EVENT_CACHE_CATALOG_ID,
  EventDataCacheService,
} from '../../../../core/services/event-data-cache.service';
import { ProcessingTimerService } from '../../../../core/services/processing-timer.service';
import { SiblingAppSyncService } from '../../../../core/services/sibling-app-sync.service';
import { autoReloadResource } from '../../../../core/utils/auto-reload-resource';
import { ApiRuntimeConfig } from '../../../../core/services/api-runtime-config';
import { CurrentTimeService } from '../../../../core/services/current-time.service';
import { SiteCurrentTimeService } from '../../../../core/services/site-current-time.service';
import { environment } from '../../../../../environments/environment';
import { tabEventRoutePath } from '../../../../core/routing/browser-tab-routes';
import {
  openSiblingAppWindow,
  RESOLVE_SYNC_WINDOW_NAME,
  siblingAppHref,
} from '../../../../core/utils/sibling-app-href';
import { EventPlaybackHostComponent } from '../event-playback-host/event-playback-host';
import { detectionVariant, formatQueueTimestamp } from '../../../../core/api/dto-mappers';
import { EventPlaybackService } from '../../services/event-playback.service';
import {
  formatPlaybackTimestamp,
  fromNvrWallClockDate,
  playbackTimeZoneAbbreviation,
  toNvrWallClockDate,
} from '../../utils/nvr-timezone.util';
import {
  CAMERAS_PLAYBACK_RANGE_SECONDS,
  compareEventAlarmTimestamps,
  findEarliestAlarm,
  findLatestAlarm,
  parseEventAlarmTimestamp,
} from '../../utils/playback-window.util';
import {
  cameraGridTotalPages,
  clampCameraGridPage,
  growLayoutForCount,
  pageCameraIds,
} from '../../utils/camera-grid.util';

/** Hub `disarmedDateTime` with no offset is a UTC instant. */
const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;
const CLOSING_DETAILS_MAX_LENGTH = 200;

@Component({
  selector: 'app-event-processing-panel',
  imports: [
    EventqEventMetaBar,
    EventqButton,
    EventqToggleTab,
    EventqDatetimePicker,
    EventqSelect,
    EventqCameraPages,
    EventqCameraListPanel,
    EventqCameraGrid,
    EventqCollapsibleSearchPanel,
    EventqCollapsibleSearchPanelBody,
    EventqTalkdownGroup,
    EventqActivateTalkdownPopover,
    EventqAccentCheckbox,
    EventqLinedTab,
    EventqDataTable,
    EventqShortcutScope,
    EventqRaiseEventPopover,
    EventqParkEventPopover,
    EventqPopoverSearch,
    EventqCloseEventPopover,
    EventqDisarmSitePopover,
    EventPlaybackHostComponent,
    EventqDialogPopover,
    EventqViewAlarmPopover,
    EventqActionLoader,
    PrefixUrlPipe,
  ],
  templateUrl: './event-processing-panel.html',
  styleUrl: './event-processing-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class EventProcessingPanel {
  private readonly tabNavigation = inject(BrowserTabNavigationService);
  private readonly agentData = inject(AgentDataService);
  private readonly processingTimers = inject(ProcessingTimerService);
  private readonly eventCache = inject(EventDataCacheService);
  private readonly playbackService = inject(EventPlaybackService);
  private readonly toast = inject(EventqToastService);
  private readonly viewedAlarms = inject(AlarmViewedStorageService);
  readonly browserTabs = inject(BrowserTabService);
  readonly access = inject(AgentAccessService);
  readonly siteClock = inject(SiteCurrentTimeService);
  readonly siblingSync = inject(SiblingAppSyncService);
  private readonly apiConfig = inject(ApiRuntimeConfig);

  private readonly siteTimezoneCancel$ = new Subject<void>();

  /** Playback can only look backwards, so the pickers stop at the current time. */
  readonly latestSelectableDate = inject(CurrentTimeService).now;

  /** Site id for live `GET /sites/{id}/devices` — event detail first, tab state until detail loads. */
  readonly eventDeviceSiteId = computed(() => {
    const fromDetail = this.detail.hasValue() ? this.detail.value()?.siteId : undefined;
    if (!this.apiConfig.useMocks() && fromDetail != null && fromDetail > 0) {
      return fromDetail;
    }
    return this.browserTabs.activePageState().activeEventSiteId;
  });

  /** Site name from event location, else the cached dealer site record. */
  private readonly cachedDealerSiteName = signal('');
  readonly eventReportSiteName = computed(() => {
    const location = this.viewDetail()?.location?.trim() ?? '';
    return location || this.cachedDealerSiteName();
  });

  readonly eventId = input.required<string>();
  readonly processingMode = input<EventProcessingMode>('process-event');

  readonly popoverSubmitAttempted = signal(false);
  readonly cameraArmDisarmSubmitting = signal(false);
  readonly cameraArmDisarmAnchor = signal<HTMLElement | null>(null);

  readonly raiseActionActive = computed(() => {
    const popover = this.browserTabs.eventViewPopover();
    const pending = this.browserTabs.pendingAction();
    return popover === 'raise-to-resolve' || pending?.type === 'raise-to-resolve';
  });

  readonly parkActionActive = computed(() => {
    const popover = this.browserTabs.eventViewPopover();
    const pending = this.browserTabs.pendingAction();
    return popover === 'park-event' || pending?.type === 'park-event';
  });

  readonly selectedParkDuration = computed(
    () => this.browserTabs.eventViewPopoverParkDurationMinutes() as ParkEventDurationMinutes,
  );

  readonly closeActionActive = computed(() => {
    const popover = this.browserTabs.eventViewPopover();
    const pending = this.browserTabs.pendingAction();
    return popover === 'close-event' || pending?.type === 'close-event';
  });

  readonly detail = resource({
    params: () => ({ eventId: this.eventId() }),
    loader: async ({ params }) => {
      if (!params.eventId) {
        return Promise.reject(new Error('missing event id'));
      }
      const detail = await this.eventCache.loadCached('detail', params.eventId, null, () =>
        firstValueFrom(this.agentData.getEventDetail(params.eventId)),
      );
      void this.applySiteTimezone(detail.siteId);
      this.applyClaimedAtFromDetail(detail);
      return detail;
    },
  });

  readonly viewDetail = computed(() => {
    if (this.detail.hasValue()) {
      return this.detail.value();
    }
    return this.eventCache.read<EventDetail>('detail', this.eventId());
  });

  /** Latest loaded alarm/camera lists keyed by eventId, used to reconcile selection once both land. */
  private loadedAlarmRows: { eventId: string; items: EventAlarmRow[] } | null = null;
  /**
   * Full alarm list for the event whose unfiltered load succeeded.
   * Interval reloads send `alarmStatus=New` and append onto this snapshot.
   */
  private loadedAlarmSnapshot: { eventId: string; response: EventAlarmsResponse } | null = null;
  private loadedCameraItems: { eventId: string; items: EventCameraItem[] } | null = null;
  /** Event the alarms clip mode currently belongs to. A different id resets that tile to clip. */
  private alarmsClipEventId: string | null = null;
  /** App tab that last loaded alarms. Distinguishes a new event from an app-tab switch. */
  private alarmsClipTabId: string | null = null;
  /** App tab that owned the alarms table the last time its scroll was synced. */
  private boundAlarmsScrollTabId: string | null = null;
  /** Table instance that already received this tab's stored scroll offset. */
  private restoredAlarmsTable: EventqDataTable | null = null;
  /**
   * The next auto-select picks the newest alarm. Set only when a refresh of the same
   * event no longer contains the alarm that was selected for that event.
   */
  private selectLatestAlarmOnNextReconcile = false;
  /** Event the current selection belonged to. A leftover id from another event is not a dropped selection. */
  private selectedAlarmEventId: string | null = null;
  /** Successful flag POSTs for the current alarm list, until the next alarms load. */
  private readonly flagUpdates = signal<Record<string, boolean>>({});
  /** Alarm ids with an in-flight flag POST (Space is ignored for these). */
  private readonly flagInFlight = signal<ReadonlySet<string>>(new Set());
  private readonly alarmsTable = viewChild<EventqDataTable>('alarmsTable');

  readonly alarms = resource({
    params: () => ({ eventId: this.eventId() }),
    loader: async ({ params, abortSignal }) => {
      if (!params.eventId) {
        return Promise.reject(new Error('missing event id'));
      }
      this.resetAlarmsClipWhenEventChanges(params.eventId);
      const requestEventId = params.eventId;
      const baseline =
        this.loadedAlarmSnapshot?.eventId === requestEventId
          ? this.loadedAlarmSnapshot.response
          : null;
      if (!baseline) {
        this.loadedAlarmRows = null;
      }
      const result = await firstValueFrom(
        this.agentData.getEventAlarms(
          requestEventId,
          baseline ? { alarmStatus: 'New', mergeInto: baseline } : undefined,
        ),
      );
      if (abortSignal.aborted) {
        return result;
      }
      if (result.flaggedLoadFailed) {
        this.toast.show('notice', {
          title: 'Flag failed',
          subtitle: 'Could not load alarm flags. Try again.',
          tone: 'error',
        });
      }
      this.eventCache.write('alarms', requestEventId, result);
      this.loadedAlarmSnapshot = { eventId: requestEventId, response: result };
      if (this.eventId() !== requestEventId) {
        return result;
      }
      this.flagUpdates.set({});
      this.loadedAlarmRows = { eventId: requestEventId, items: result.items };
      this.reconcileAlarmCameraSelection();
      return result;
    },
  });

  /** Cameras and speakers belong to the site, so they reload when the event's site resolves. */
  private readonly siteId = computed(() => this.eventDeviceSiteId());

  readonly cameras = resource({
    params: () => ({ eventId: this.eventId(), siteId: this.siteId() }),
    loader: async ({ params }) => {
      const requestEventId = params.eventId;
      this.loadedCameraItems = null;
      const result = await this.eventCache.loadCached(
        'cameras',
        requestEventId,
        params.siteId,
        () => firstValueFrom(this.agentData.getEventCameras(params.siteId)),
      );
      if (this.eventId() !== requestEventId) {
        return result;
      }
      this.loadedCameraItems = { eventId: requestEventId, items: result.items };
      this.reconcileAlarmCameraSelection();
      return result;
    },
  });

  readonly talkdowns = resource({
    params: () => ({ eventId: this.eventId(), siteId: this.siteId() }),
    loader: ({ params }) =>
      this.eventCache.loadCached('talkdowns', params.eventId, params.siteId, () =>
        firstValueFrom(this.agentData.getEventTalkdowns(params.siteId)),
      ),
  });

  readonly camerasPlaybackAnchorAt = computed(
    () => this.browserTabs.activePageState().camerasPlaybackAnchorAt,
  );

  readonly camerasPlaybackTimeZone = computed(() => this.resolveCamerasPlaybackTimeZone());

  readonly camerasPlaybackAnchorDate = computed(() => {
    const iso = this.camerasPlaybackAnchorAt();
    if (!iso) {
      return null;
    }
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return toNvrWallClockDate(parsed, this.camerasPlaybackTimeZone());
  });

  readonly camerasPlaybackPickerMax = computed(() =>
    toNvrWallClockDate(this.latestSelectableDate(), this.camerasPlaybackTimeZone()),
  );

  readonly camerasPlaybackTimeZoneAbbreviation = computed(() => {
    const iso = this.camerasPlaybackAnchorAt();
    return playbackTimeZoneAbbreviation(
      this.camerasPlaybackTimeZone(),
      iso ? new Date(iso) : this.latestSelectableDate(),
    );
  });

  readonly showParkAction = computed(() => {
    const startedAs = this.viewDetail()?.type;
    const parkableEvent = startedAs !== 'manual-patrol' && startedAs !== 'site-check';
    return this.processingMode() === 'process-event' && parkableEvent && this.access.canPark();
  });

  /** Playback-tab grid only. The alarms clip tile must keep its own Clip/Playback state. */
  private readonly useCamerasPlaybackAnchor = computed(
    () => this.browserTabs.eventViewTab() === 'playback' && !!this.camerasPlaybackAnchorAt(),
  );

  readonly anchoredPlaybackViewMode = computed<PlaybackViewMode>(() =>
    this.useCamerasPlaybackAnchor()
      ? 'playback'
      : this.browserTabs.activePageState().playbackViewMode,
  );

  readonly anchoredPlaybackCustomRangeSeconds = computed(() =>
    this.useCamerasPlaybackAnchor()
      ? CAMERAS_PLAYBACK_RANGE_SECONDS
      : this.browserTabs.activePageState().playbackCustomRangeSeconds,
  );

  readonly anchoredPlaybackTimestamp = computed(() =>
    this.useCamerasPlaybackAnchor()
      ? this.camerasPlaybackAnchorAt()
      : this.browserTabs.activePageState().playbackAnchorTimestamp,
  );

  readonly anchoredPlaybackAnchorMode = computed<'center' | 'start' | 'end'>(() =>
    this.useCamerasPlaybackAnchor() ? 'end' : 'center',
  );

  readonly raiseOutcomes = resource({
    params: () => ({
      open: this.browserTabs.eventViewPopover() === 'raise-to-resolve',
    }),
    loader: ({ params }) => {
      if (!params.open) {
        return Promise.resolve(
          this.eventCache.read<EventOutcomesResponse>('raiseOutcomes', EVENT_CACHE_CATALOG_ID) ?? {
            groups: [],
          },
        );
      }
      return this.eventCache.loadCached('raiseOutcomes', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getRaiseEventOutcomes()),
      );
    },
  });

  readonly closeOutcomes = resource({
    params: () => ({
      open: this.browserTabs.eventViewPopover() === 'close-event',
    }),
    loader: ({ params }) => {
      if (!params.open) {
        return Promise.resolve(
          this.eventCache.read<EventOutcomesResponse>('closeOutcomes', EVENT_CACHE_CATALOG_ID) ?? {
            groups: [],
          },
        );
      }
      return this.eventCache.loadCached('closeOutcomes', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getCloseEventOutcomes()),
      );
    },
  });

  readonly cameraDisarmReasons = resource({
    params: () => ({
      open: this.browserTabs.activePageState().cameraArmDisarmPopover === 'disarm',
    }),
    loader: ({ params }) => {
      if (!params.open) {
        return Promise.resolve(
          this.eventCache.read<SiteDisarmReasonsResponse>(
            'cameraDisarmReasons',
            EVENT_CACHE_CATALOG_ID,
          ) ?? { items: [] },
        );
      }
      return this.eventCache.loadCached('cameraDisarmReasons', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getCameraDisarmReasons()),
      );
    },
  });

  readonly mainTabs = [
    { id: 'alarms', label: 'Alarms', iconSrc: '/assets/events/icons/alarms.svg' },
    {
      id: 'live-cameras',
      label: 'Live Cameras',
      iconSrc: '/assets/events/icons/live-cameras.svg',
    },
    { id: 'playback', label: 'Playback', iconSrc: '/assets/events/icons/playback.svg' },
  ];

  readonly cameraLayoutOptions: readonly EventqSelectOption[] = [
    { id: '4x3', label: '4x3' },
    { id: '3x3', label: '3x3' },
    { id: '2x2', label: '2x2' },
    { id: '1x1', label: '1x1' },
  ];

  readonly showCameraGridChrome = computed(
    () =>
      this.browserTabs.eventViewTab() === 'live-cameras' ||
      this.browserTabs.eventViewTab() === 'playback',
  );

  readonly cameraListOpen = computed(() => this.browserTabs.activePageState().cameraListOpen);
  readonly cameraGridLayout = computed(() =>
    normalizeCameraGridLayout(this.browserTabs.activePageState().cameraGridLayout),
  );
  readonly cameraGridPage = computed(() => this.browserTabs.activePageState().cameraGridPage);
  readonly selectedCameraIds = computed(() => this.browserTabs.activePageState().selectedCameraIds);
  readonly cameraGridSearchQuery = computed(
    () => this.browserTabs.activePageState().cameraGridSearchQuery,
  );

  readonly cameraGridListItems = computed(() => {
    const query = this.cameraGridSearchQuery().trim().toLowerCase();
    return this.eventCameras()
      .filter((camera) => !query || camera.name.toLowerCase().includes(query))
      .map((camera) => ({ id: camera.id, label: camera.name }));
  });

  readonly cameraGridTotalPages = computed(() =>
    cameraGridTotalPages(this.selectedCameraIds().length, this.cameraGridLayout()),
  );

  readonly pageCameraIds = computed(() =>
    pageCameraIds(this.selectedCameraIds(), this.cameraGridLayout(), this.cameraGridPage()),
  );

  readonly gridLayout = computed(() => this.cameraGridLayout() as EventqCameraGridLayout);

  readonly alarmColumns: EventqDataTableColumn[] = [
    { key: 'indicator', label: '', width: '32px', align: 'center', cellType: 'indicator' },
    { key: 'timestamp', label: 'Alarm Time', width: 'minmax(0, 226fr)', sortable: true },
    { key: 'camera', label: 'Linked Camera', width: 'minmax(0, 223fr)', sortable: true },
    { key: 'aiTags', label: 'AI Events', width: 'minmax(0, 301fr)', cellType: 'aiTags' },
    {
      key: 'flagged',
      label: 'Flag Alarm',
      width: 'minmax(0, 1114fr)',
      align: 'start',
      cellType: 'flag',
    },
  ];

  readonly alarmSort = computed(() => this.browserTabs.activePageState().alarmSort);

  readonly canArmDisarmCamera = this.access.canArmDisarmCamera;

  readonly armDisarmSort = computed(() => this.browserTabs.activePageState().armDisarmSort);

  readonly armDisarmColumns = computed((): EventqDataTableColumn[] => {
    const columns: EventqDataTableColumn[] = [
      {
        key: 'status',
        label: 'Status',
        width: '72px',
        align: 'center',
        cellType: 'armStatus',
      },
      { key: 'name', label: 'Cameras', grow: 1.2, sortable: true },
    ];

    if (!this.canArmDisarmCamera()) {
      return columns;
    }

    return [
      ...columns,
      { key: 'disarmingNotes', label: 'Disarming Notes', grow: 1.6 },
      { key: 'disarmedAt', label: 'Disarmed At', grow: 1, sortable: true },
      { key: 'disarmedBy', label: 'Disarmed By', grow: 1 },
    ];
  });

  readonly armDisarmSearchQuery = computed(
    () => this.browserTabs.activePageState().armDisarmSearchQuery ?? '',
  );

  readonly armDisarmEmptyLabel = computed(() =>
    this.armDisarmSearchQuery().trim() ? 'No results' : 'No Cameras in this site.',
  );

  readonly armDisarmRows = computed(() => {
    const query = this.armDisarmSearchQuery().trim().toLowerCase();
    const rows = this.eventCameras().map((camera) => this.toArmDisarmRow(camera));
    const filtered = query
      ? rows.filter((row) => this.armDisarmRowMatches(row, query))
      : rows;
    return this.sortArmDisarmRows(filtered, this.armDisarmSort());
  });

  readonly selectedCameraArmDisarm = computed(() => {
    const cameraId = this.browserTabs.activePageState().cameraArmDisarmCameraId;
    if (!cameraId) {
      return null;
    }
    return this.eventCameras().find((camera) => camera.id === cameraId) ?? null;
  });

  readonly cameraDisarmPopoverOpen = computed(
    () => this.browserTabs.activePageState().cameraArmDisarmPopover === 'disarm',
  );

  readonly activateTalkdownOpen = computed(
    () => this.browserTabs.activePageState().activateTalkdownGroupId !== null,
  );

  readonly activateTalkdownTitle = computed(() => {
    const groupId = this.browserTabs.activePageState().activateTalkdownGroupId;
    if (!groupId) {
      return 'Activate Talkdown';
    }

    const items = this.talkdowns.value()?.items ?? [];
    const index = items.findIndex((group) => group.id === groupId);
    return index >= 0 ? `Activate Talkdown ${index + 1}` : 'Activate Talkdown';
  });

  readonly activateTalkdownOptions = computed((): readonly ActivateTalkdownOption[] => {
    const groupId = this.browserTabs.activePageState().activateTalkdownGroupId;
    if (!groupId) {
      return ACTIVATE_TALKDOWN_OPTIONS;
    }

    const group = (this.talkdowns.value()?.items ?? []).find((item) => item.id === groupId);
    if (group?.messageOptions?.length) {
      return group.messageOptions.map((message) => ({
        id: message.id as ActivateTalkdownConfirm['optionId'],
        labelLines: message.labelLines,
      }));
    }

    if (this.apiConfig.useMocks()) {
      return ACTIVATE_TALKDOWN_OPTIONS;
    }

    return [];
  });

  readonly siteDisarmReasonId = computed(
    () => this.browserTabs.activePageState().siteDisarmReasonId,
  );
  readonly siteDisarmFor = computed(() => this.browserTabs.activePageState().siteDisarmFor);
  readonly siteDisarmFixedDurationMinutes = computed(
    () => this.browserTabs.activePageState().siteDisarmFixedDurationMinutes,
  );
  readonly siteDisarmFixedDurationCustom = computed(
    () => this.browserTabs.activePageState().siteDisarmFixedDurationCustom,
  );
  readonly siteDisarmFixedDurationCustomDraft = computed(
    () => this.browserTabs.activePageState().siteDisarmFixedDurationCustomDraft,
  );
  readonly siteDisarmFixedDurationMinutesForUi = computed(() =>
    resolveDisplayedDisarmFixedDurationMinutes(
      this.siteDisarmFixedDurationMinutes(),
      this.siteDisarmFixedDurationCustom(),
    ),
  );
  readonly siteDisarmUntilAt = computed(() => this.browserTabs.activePageState().siteDisarmUntilAt);

  readonly filteredCameras = computed(() => {
    const query = this.browserTabs.activePageState().cameraSearchQuery.trim().toLowerCase();
    const items = this.eventCameras();
    if (!query) {
      return items.map((c) => ({ id: c.id, label: c.name }));
    }
    return items
      .filter((c) => c.name.toLowerCase().includes(query))
      .map((c) => ({ id: c.id, label: c.name }));
  });

  readonly filteredTalkdowns = computed(() => {
    const query = this.browserTabs.activePageState().talkdownSearchQuery.trim().toLowerCase();
    const items = this.talkdowns.value()?.items ?? [];
    if (!query) {
      return items;
    }
    return items.filter(
      (td) =>
        td.speakerName.toLowerCase().includes(query) ||
        td.cameraLabels.some((l) => l.toLowerCase().includes(query)),
    );
  });

  /** Alarms carry only a cameraId, so the display name comes from the site's device list. */
  private readonly cameraNamesById = computed(
    () => new Map(this.eventCameras().map((camera) => [camera.id, camera.name])),
  );

  /** `resource.value()` rethrows in the error state, so a failed list reads as empty instead. */
  readonly alarmItems = computed(() => {
    if (this.alarms.hasValue()) {
      return this.alarms.value()?.items ?? [];
    }
    return this.eventCache.read<EventAlarmsResponse>('alarms', this.eventId())?.items ?? [];
  });

  readonly alarmRows = computed(() => {
    const filters = this.browserTabs.alarmFilters();
    const cameraNames = this.cameraNamesById();
    const eventId = this.eventId();
    const viewed = this.viewedAlarms.viewedIdSet(eventId);
    const flagOverrides = this.flagUpdates();
    // Every alarm on the event is listed — side-panel camera selection is live-only.
    const rows = this.alarmItems()
      .filter((alarm) => this.matchesFilter(alarm, filters))
      .map((alarm) => {
        const override = flagOverrides[alarm.id];
        return {
          id: alarm.id,
          // Table [viewed] is this boolean: false = unviewed red disc.
          indicator: viewed.has(alarm.id),
          timestamp: alarm.timestamp,
          camera: cameraNames.get(alarm.cameraId) ?? alarm.camera,
          aiTags: alarm.aiTags,
          flagged: override !== undefined ? override : alarm.flagged,
        };
      });

    return this.sortAlarmRows(rows, this.alarmSort()).map((row) => ({
      ...row,
      timestamp: this.formatSiteTimestamp(row.timestamp),
    }));
  });

  readonly unflaggedCount = computed(() => this.alarmRows().filter((row) => !row.flagged).length);

  private readonly flagAllSubmitting = signal(false);

  readonly flagAllDisabled = computed(
    () => this.flagAllSubmitting() || (this.alarms.isLoading() && this.alarmItems().length === 0),
  );

  readonly eventCameras = computed(() => {
    if (this.cameras.hasValue()) {
      return this.cameras.value()?.items ?? [];
    }
    return (
      this.eventCache.read<EventCamerasResponse>('cameras', this.eventId(), this.siteId())?.items ??
      []
    );
  });

  readonly selectedCameraId = computed(() => this.browserTabs.activePageState().selectedCameraId);

  readonly playbackCameraId = computed(() => {
    const alarmId = this.browserTabs.activePageState().selectedAlarmId;
    if (alarmId) {
      const alarm = (this.alarmItems()).find((item) => item.id === alarmId);
      if (alarm?.cameraId) {
        // Alarm clip/playback is owned by the alarm's camera — never the side-panel live pick.
        const camera = this.playbackService.resolveCameraById(this.eventCameras(), alarm.cameraId);
        return camera?.id ?? alarm.cameraId.replace(/^cam-/, '');
      }
    }
    return this.selectedCameraId();
  });

  readonly liveCameraId = computed(() => this.selectedCameraId());

  /** Selected alarm, or the earliest event alarm so attach-clip can POST without a row click. */
  readonly attachClipAlarmId = computed(() => {
    const selected = this.browserTabs.activePageState().selectedAlarmId?.trim();
    if (selected) {
      return selected;
    }
    return findEarliestAlarm(this.alarmItems())?.id ?? null;
  });

  readonly selectedAlarmClipUrl = computed(() =>
    this.clipUrlForAlarm(this.browserTabs.activePageState().selectedAlarmId),
  );

  readonly selectedAlarmClipUrls = computed(
    () => this.selectedAlarmContext()?.clipUrls?.filter((url) => !!url?.trim()) ?? [],
  );

  readonly selectedAlarmContext = computed(() => {
    const alarmId = this.browserTabs.activePageState().selectedAlarmId;
    if (!alarmId) {
      return null;
    }
    return (this.alarmItems()).find((item) => item.id === alarmId) ?? null;
  });

  readonly selectedAlarmDetections = computed(
    () => this.selectedAlarmContext()?.detections ?? [],
  );

  readonly selectedAlarmTimestamp = computed(
    () => this.selectedAlarmContext()?.timestamp ?? null,
  );

  readonly viewAlarmAlarmContext = computed(() => {
    const alarmId = this.viewAlarmSelectedAlarmId();
    if (!alarmId) {
      return null;
    }
    return (this.alarmItems()).find((item) => item.id === alarmId) ?? null;
  });

  readonly viewAlarmClipUrl = computed(() =>
    this.clipUrlForAlarm(this.browserTabs.activePageState().viewAlarmSelectedAlarmId),
  );

  readonly viewAlarmClipUrls = computed(
    () => this.viewAlarmAlarmContext()?.clipUrls?.filter((url) => !!url?.trim()) ?? [],
  );

  readonly playbackCamera = computed(() =>
    this.playbackService.resolveCameraById(this.eventCameras(), this.playbackCameraId()),
  );

  readonly liveCamera = computed(() =>
    this.playbackService.resolveCameraById(this.eventCameras(), this.liveCameraId()),
  );

  readonly liveCameraPopoutCameraId = computed(
    () => this.browserTabs.activePageState().liveCameraPopoutCameraId,
  );

  readonly liveCameraPopoutCamera = computed(() =>
    this.playbackService.resolveCameraById(this.eventCameras(), this.liveCameraPopoutCameraId()),
  );

  readonly playbackHeaderTitle = computed(() => {
    const alarmId = this.browserTabs.activePageState().selectedAlarmId;
    const camera = this.playbackCamera();
    if (alarmId && camera) {
      return `Alarm Clip ${camera.name}`;
    }
    return camera?.name ?? 'Camera Playback';
  });

  readonly playbackHeaderTimestamp = computed(() => {
    const anchorIso = this.browserTabs.activePageState().playbackAnchorTimestamp;
    if (anchorIso) {
      const parsed = new Date(anchorIso);
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatPlaybackCameraTimestamp(anchorIso, this.playbackCamera()?.timeZone);
      }
    }

    const alarmId = this.browserTabs.activePageState().selectedAlarmId;
    if (!alarmId) {
      return undefined;
    }
    const timestamp = (this.alarmItems()).find((item) => item.id === alarmId)
      ?.timestamp;
    return timestamp
      ? this.formatPlaybackCameraTimestamp(timestamp, this.playbackCamera()?.timeZone)
      : undefined;
  });

  readonly playbackVisibleDetectionTypes = computed(() => {
    const alarmId = this.browserTabs.activePageState().selectedAlarmId;
    if (!alarmId) {
      return [] as EventAiTagVariant[];
    }
    const alarm = (this.alarmItems()).find((item) => item.id === alarmId);
    if (!alarm) {
      return [] as EventAiTagVariant[];
    }
    if (alarm.aiTags.length > 0) {
      return alarm.aiTags.map((tag) => tag.variant);
    }
    return [
      ...new Set(
        (alarm.detections ?? []).map((detection) => detectionVariant(detection.name, 'motion')),
      ),
    ];
  });

  readonly viewAlarmOpen = computed(() => this.browserTabs.activePageState().viewAlarmOpen);

  readonly viewAlarmSelectedAlarmId = computed(
    () => this.browserTabs.activePageState().viewAlarmSelectedAlarmId,
  );

  readonly alarmsTableSelectedRowId = computed(() => {
    if (this.viewAlarmOpen()) {
      return this.viewAlarmSelectedAlarmId();
    }
    return this.browserTabs.activePageState().selectedAlarmId;
  });

  readonly viewAlarmCameraId = computed(() => {
    const alarmId = this.viewAlarmSelectedAlarmId();
    if (!alarmId) {
      return null;
    }
    const alarm = (this.alarmItems()).find((item) => item.id === alarmId);
    if (!alarm) {
      return null;
    }
    if (alarm.cameraId) {
      return alarm.cameraId;
    }
    return this.playbackService.resolveCameraByName(this.eventCameras(), alarm.camera)?.id ?? null;
  });

  readonly viewAlarmCamera = computed(() =>
    this.playbackService.resolveCameraById(this.eventCameras(), this.viewAlarmCameraId()),
  );

  readonly viewAlarmHeaderTitle = computed(() => {
    const camera = this.viewAlarmCamera();
    const alarmId = this.viewAlarmSelectedAlarmId();
    if (alarmId && camera) {
      return `Alarm Clip ${camera.name}`;
    }
    return camera?.name ?? 'Alarm';
  });

  readonly viewAlarmHeaderTimestamp = computed(() => {
    const anchorIso = this.browserTabs.activePageState().viewAlarmPlaybackAnchorTimestamp;
    if (anchorIso) {
      const parsed = new Date(anchorIso);
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatPlaybackCameraTimestamp(anchorIso, this.viewAlarmCamera()?.timeZone);
      }
    }

    const alarmId = this.viewAlarmSelectedAlarmId();
    if (!alarmId) {
      return undefined;
    }
    const timestamp = (this.alarmItems()).find((item) => item.id === alarmId)
      ?.timestamp;
    return timestamp
      ? this.formatPlaybackCameraTimestamp(timestamp, this.viewAlarmCamera()?.timeZone)
      : undefined;
  });

  readonly viewAlarmVisibleDetectionTypes = computed(() => {
    const alarmId = this.viewAlarmSelectedAlarmId();
    if (!alarmId) {
      return [] as EventAiTagVariant[];
    }
    const alarm = (this.alarmItems()).find((item) => item.id === alarmId);
    if (!alarm) {
      return [] as EventAiTagVariant[];
    }
    if (alarm.aiTags.length > 0) {
      return alarm.aiTags.map((tag) => tag.variant);
    }
    return [
      ...new Set(
        (alarm.detections ?? []).map((detection) => detectionVariant(detection.name, 'motion')),
      ),
    ];
  });

  readonly viewAlarmSelectedIndex = computed(() => {
    const alarmId = this.viewAlarmSelectedAlarmId();
    if (!alarmId) {
      return -1;
    }
    return this.alarmRows().findIndex((row) => String(row.id) === alarmId);
  });

  readonly viewAlarmHasPrev = computed(() => this.viewAlarmSelectedIndex() > 0);

  readonly viewAlarmHasNext = computed(() => {
    const index = this.viewAlarmSelectedIndex();
    return index >= 0 && index < this.alarmRows().length - 1;
  });

  readonly viewAlarmFlagged = computed(() => {
    const alarmId = this.viewAlarmSelectedAlarmId();
    return alarmId ? this.isAlarmFlagged(alarmId) : false;
  });

  readonly viewAlarmUrl = computed(() => {
    const eventId = this.eventId();
    const camera = this.viewAlarmCamera();
    const timestamp = this.viewAlarmHeaderTimestamp();
    if (!eventId || !camera) {
      return '';
    }
    const slug = timestamp?.replace(/[^\d]/g, '').slice(0, 14) ?? 'live';
    return `events.ecam.com/${eventId}/${camera.name.replace(/\s+/g, '-').toLowerCase()}/${slug}`;
  });

  constructor() {
    afterRenderEffect(() => {
      const tabId = this.browserTabs.activeTabId();
      const table = this.alarmsTable();
      untracked(() => this.syncAlarmsTableScroll(tabId, table));
    });

    autoReloadResource(this.alarms, environment.refreshIntervalMs, { pollWhileHidden: true });
    autoReloadResource(
      {
        reload: () => {
          void this.refreshCamerasFromDealer();
          return true;
        },
      },
      environment.refreshIntervalMs,
    );

    effect(() => {
      const event = this.viewDetail();
      if (this.detail.error() && !event) {
        return;
      }
      if (!event) {
        return;
      }
      const location = event.location;
      const items = this.alarmItems();
      const tagLabels = [
        ...new Set(items.flatMap((alarm) => alarm.aiTags.map((tag) => tag.label))),
      ];

      if (location || tagLabels.length) {
        this.browserTabs.updateTabDisplay(this.browserTabs.activeTabId(), {
          ...(location ? { label: location } : {}),
          ...(tagLabels.length ? { tags: tagLabels } : {}),
        });
      }
    });

    effect(() => {
      if (!this.detail.error()) {
        return;
      }
      void this.tabNavigation.navigateWithinActiveTab();
    });

    effect(() => {
      if (!this.eventId()) {
        this.siteClock.setTimezone(null);
      }
    });
  }

  private applyClaimedAtFromDetail(detail: EventDetail): void {
    const claimedAt = detail.claimedAt?.trim();
    if (!claimedAt) {
      return;
    }

    this.browserTabs.updateActivePageState({ claimedAt });
    this.processingTimers.startForTab(this.browserTabs.activeTabId(), {
      entityId: this.eventId(),
      entityType: this.processingMode(),
      claimedAt,
    });
  }

  private async applySiteTimezone(siteId: number | null | undefined): Promise<void> {
    this.siteTimezoneCancel$.next();
    if (!siteId) {
      this.cachedDealerSiteName.set('');
      this.siteClock.setTimezone(null);
      return;
    }
    try {
      const site = await this.eventCache.loadCached('site', String(siteId), null, async () => {
        const loaded = await firstValueFrom(
          this.agentData.getDealerSite(siteId).pipe(
            catchError(() => of(null)),
            takeUntil(this.siteTimezoneCancel$),
          ),
        );
        if (!loaded) {
          throw new Error('site unavailable');
        }
        return loaded;
      });
      this.cachedDealerSiteName.set(site.siteName?.trim() ?? '');
      const tz = site.timezone?.trim();
      this.siteClock.setTimezone(tz || null);
    } catch {
      this.cachedDealerSiteName.set('');
      this.siteClock.setTimezone(null);
    }
  }

  /**
   * The first load of an event keeps the tab's clip/playback choice (including a refresh of
   * the event that is already open). A later event id returns the alarms tile to clip mode.
   */
  private resetAlarmsClipWhenEventChanges(eventId: string): void {
    const previousEventId = this.alarmsClipEventId;
    const previousTabId = this.alarmsClipTabId;
    const tabId = this.browserTabs.activeTabId();
    this.alarmsClipEventId = eventId;
    this.alarmsClipTabId = tabId;
    if (!previousEventId || previousEventId === eventId) {
      return;
    }

    this.selectLatestAlarmOnNextReconcile = false;
    this.selectedAlarmEventId = null;
    // A different event on this tab is a new list. Switching app tabs also changes
    // the event id, and that tab already has its own scroll offset.
    const sameTab = previousTabId === tabId;
    this.browserTabs.updateActivePageState({
      ...createAlarmsClipModeReset(),
      selectedAlarmId: null,
      ...(sameTab ? { alarmsTableScrollTop: 0 } : {}),
    });
    if (sameTab) {
      this.alarmsTable()?.restoreScrollOffset(0);
    }
  }

  /**
   * Called by the alarms/cameras loaders as each finishes. Both lists are read from the fields
   * above rather than `resource.value()`, which is not published until the loader resolves —
   * whichever loader finishes second performs the auto-selection pass with both lists present.
   */
  private reconcileAlarmCameraSelection(): void {
    const eventId = this.eventId();
    const loadedAlarms =
      this.loadedAlarmRows?.eventId === eventId ? this.loadedAlarmRows.items : null;
    const loadedCameras =
      this.loadedCameraItems?.eventId === eventId ? this.loadedCameraItems.items : null;
    const alarms = loadedAlarms ?? [];
    const state = this.browserTabs.activePageState();

    if (loadedAlarms) {
      if (state.selectedAlarmId && !alarms.some((alarm) => alarm.id === state.selectedAlarmId)) {
        if (this.selectedAlarmEventId === eventId) {
          this.selectLatestAlarmOnNextReconcile = true;
        }
        this.browserTabs.updateActivePageState({ selectedAlarmId: null });
      }

      if (
        state.viewAlarmSelectedAlarmId &&
        !alarms.some((alarm) => alarm.id === state.viewAlarmSelectedAlarmId)
      ) {
        this.browserTabs.updateActivePageState({ viewAlarmSelectedAlarmId: null });
      }

      this.maybeSeedCamerasPlaybackAnchor();
    }

    if (!eventId || !loadedCameras?.length) {
      return;
    }

    const cameras = loadedCameras;

    // Show a camera immediately so the live tile is not black while alarms are still loading.
    if (!cameras.some((camera) => camera.id === state.selectedCameraId)) {
      this.browserTabs.updateActivePageState({ selectedCameraId: cameras[0].id });
    }

    this.maybeSeedLiveCameraFromEarliestAlarm(eventId, cameras, loadedAlarms);

    // Tiles render black without a camera, so the first camera is picked as soon as the camera
    // list lands — the alarms list may be empty, still loading, or have failed outright.
    if (!alarms.length) {
      return;
    }

    const latestState = this.browserTabs.activePageState();
    if (
      latestState.selectedAlarmId &&
      alarms.some((alarm) => alarm.id === latestState.selectedAlarmId)
    ) {
      this.selectedAlarmEventId = eventId;
      this.selectLatestAlarmOnNextReconcile = false;
      return;
    }

    const pickLatest = this.selectLatestAlarmOnNextReconcile;
    this.selectLatestAlarmOnNextReconcile = false;
    // Opening an event starts on the earliest alarm. A same-event refresh that dropped
    // the selected id moves to the newest alarm still in the list.
    const nextAlarm = pickLatest ? findLatestAlarm(alarms) : findEarliestAlarm(alarms);
    if (nextAlarm) {
      this.selectedAlarmEventId = eventId;
      this.browserTabs.updateActivePageState({ selectedAlarmId: nextAlarm.id });
    }
  }

  /**
   * Once per event: upgrade the live tile from the first-in-list fallback to the camera
   * linked to the earliest alarm. A later poll or a new earlier alarm must not move it.
   */
  private maybeSeedLiveCameraFromEarliestAlarm(
    eventId: string,
    cameras: EventCameraItem[],
    loadedAlarms: EventAlarmRow[] | null,
  ): void {
    const state = this.browserTabs.activePageState();
    if (state.liveCameraSeededEventId === eventId || !loadedAlarms?.length) {
      return;
    }

    const earliest = findEarliestAlarm(loadedAlarms);
    const linked = this.resolveLiveCameraForAlarm(cameras, earliest) ?? cameras[0];
    if (!linked) {
      return;
    }

    this.browserTabs.updateActivePageState({
      selectedCameraId: linked.id,
      liveCameraSeededEventId: eventId,
    });
  }

  private resolveLiveCameraForAlarm(
    cameras: EventCameraItem[],
    alarm: EventAlarmRow | null,
  ): EventCameraItem | null {
    if (!alarm) {
      return null;
    }
    if (alarm.cameraId) {
      const byId = this.playbackService.resolveCameraById(cameras, alarm.cameraId);
      if (byId) {
        return byId;
      }
    }
    return this.playbackService.resolveCameraByName(cameras, alarm.camera);
  }

  onCamerasPanelToggle(open: boolean): void {
    this.browserTabs.updateActivePageState({ sidePanel: open ? 'cameras' : 'talkdowns' });
  }

  onTalkdownsPanelToggle(open: boolean): void {
    this.browserTabs.updateActivePageState({ sidePanel: open ? 'talkdowns' : 'cameras' });
  }

  onMainTabChange(tabId: string): void {
    this.captureAlarmsTableScroll();
    const nextTab = tabId as 'alarms' | 'live-cameras' | 'playback';
    const updates: Parameters<BrowserTabService['updateActivePageState']>[0] = {
      eventViewTab: nextTab,
    };

    if (nextTab === 'playback') {
      const anchor = this.resolveCamerasPlaybackAnchorIso();
      if (anchor) {
        updates.camerasPlaybackAnchorAt = anchor;
        updates.playbackStreamStartMsByCameraId = {};
      }
    }

    this.browserTabs.updateActivePageState(updates);
  }

  onToggleCameraList(): void {
    this.browserTabs.updateActivePageState({
      cameraListOpen: !this.browserTabs.activePageState().cameraListOpen,
    });
  }

  onCameraGridSearch(query: string): void {
    this.browserTabs.updateActivePageState({ cameraGridSearchQuery: query });
  }

  onCameraGridLayoutChange(layoutId: string): void {
    const layout = normalizeCameraGridLayout(layoutId);
    const total = cameraGridTotalPages(this.selectedCameraIds().length, layout);
    this.browserTabs.updateActivePageState({
      cameraGridLayout: layout,
      cameraGridPage: clampCameraGridPage(this.cameraGridPage(), total),
    });
  }

  onCameraGridPageChange(page: number): void {
    this.browserTabs.updateActivePageState({
      cameraGridPage: clampCameraGridPage(page, this.cameraGridTotalPages()),
    });
  }

  onToggleGridCamera(cameraId: string): void {
    const current = this.selectedCameraIds();
    const isAdding = !current.includes(cameraId);
    const next = isAdding ? [...current, cameraId] : current.filter((id) => id !== cameraId);
    const layout = isAdding
      ? growLayoutForCount(this.cameraGridLayout(), next.length)
      : this.cameraGridLayout();
    const total = cameraGridTotalPages(next.length, layout);
    this.browserTabs.updateActivePageState({
      selectedCameraIds: next,
      cameraGridLayout: layout,
      cameraGridPage: clampCameraGridPage(this.cameraGridPage(), total),
      // Closing a tile drops its time so reopening follows the global cameras date.
      playbackStreamStartMsByCameraId: this.withoutTileStreamStarts([cameraId]),
    });
  }

  onOpenAllCameras(): void {
    const ids = this.eventCameras().map((camera) => camera.id);
    const layout = growLayoutForCount(this.cameraGridLayout(), ids.length);
    const opened = this.selectedCameraIds();
    this.browserTabs.updateActivePageState({
      selectedCameraIds: ids,
      cameraGridLayout: layout,
      cameraGridPage: 1,
      // Tiles opening now start from the global cameras date; keep live tiles as they are.
      playbackStreamStartMsByCameraId: this.withoutTileStreamStarts(
        ids.filter((id) => !opened.includes(id)),
      ),
    });
  }

  private withoutTileStreamStarts(cameraIds: readonly string[]): Record<string, number> {
    const remaining = { ...this.browserTabs.activePageState().playbackStreamStartMsByCameraId };
    for (const cameraId of cameraIds) {
      delete remaining[cameraId];
    }
    return remaining;
  }

  cameraById(cameraId: string) {
    return this.eventCameras().find((camera) => camera.id === cameraId) ?? null;
  }

  onAlarmsFooterTabChange(tab: AlarmsFooterTab): void {
    if (tab !== 'alarms') {
      this.captureAlarmsTableScroll();
    }
    this.browserTabs.updateActivePageState({ alarmsFooterTab: tab });
  }

  onAlarmsTableScroll(offset: number): void {
    if (!Number.isFinite(offset) || offset < 0) {
      return;
    }
    const tabId = this.boundAlarmsScrollTabId ?? this.browserTabs.activeTabId();
    this.browserTabs.updatePageState(tabId, { alarmsTableScrollTop: offset });
  }

  /** Writes the on-screen offset before the alarms table is removed. */
  private captureAlarmsTableScroll(): void {
    const table = this.alarmsTable();
    if (!table || this.browserTabs.eventViewTab() !== 'alarms') {
      return;
    }
    if (this.browserTabs.activePageState().alarmsFooterTab !== 'alarms') {
      return;
    }
    const tabId = this.boundAlarmsScrollTabId ?? this.browserTabs.activeTabId();
    this.browserTabs.updatePageState(tabId, { alarmsTableScrollTop: table.scrollOffset() });
  }

  /**
   * App-tab switches reuse this panel. Save the previous tab's pixels, then
   * move the table to the newly active tab's offset. A recreated table (view
   * or Disarm footer) restores once when it appears.
   */
  private syncAlarmsTableScroll(tabId: string, table: EventqDataTable | undefined): void {
    const previousTabId = this.boundAlarmsScrollTabId;
    const tabChanged = previousTabId !== null && previousTabId !== tabId;

    if (tabChanged && table) {
      this.browserTabs.updatePageState(previousTabId, {
        alarmsTableScrollTop: table.scrollOffset(),
      });
    }

    this.boundAlarmsScrollTabId = tabId;

    if (!table) {
      this.restoredAlarmsTable = null;
      return;
    }

    if (tabChanged || this.restoredAlarmsTable !== table) {
      this.restoredAlarmsTable = table;
      table.restoreScrollOffset(this.browserTabs.getPageState(tabId).alarmsTableScrollTop);
    }
  }

  onArmDisarmSort(sort: EventqSortState): void {
    this.browserTabs.updateActivePageState({ armDisarmSort: sort });
  }

  onArmDisarmSearch(query: string): void {
    this.browserTabs.updateActivePageState({ armDisarmSearchQuery: query });
  }

  onArmStatusClick(event: { row: EventqDataTableRow; anchor: HTMLElement }): void {
    this.openCameraArmDisarmFlow(event.row, event.anchor);
  }

  onArmDisarmRowClick(row: EventqDataTableRow): void {
    this.openCameraArmDisarmFlow(row, null);
  }

  private openCameraArmDisarmFlow(row: EventqDataTableRow, anchor: HTMLElement | null): void {
    if (!this.canArmDisarmCamera()) {
      return;
    }

    const cameraId = String(row.id ?? '');
    if (!cameraId) {
      return;
    }

    if (row['status'] === 'disarmed') {
      return;
    }

    this.cameraArmDisarmAnchor.set(anchor);
    this.browserTabs.updateActivePageState({
      cameraArmDisarmPopover: 'disarm',
      cameraArmDisarmCameraId: cameraId,
      siteDisarmReasonId: null,
      siteDisarmFor: 'until-next-schedule',
      siteDisarmFixedDurationMinutes: null,
      siteDisarmFixedDurationCustom: false,
      siteDisarmFixedDurationCustomDraft: '',
      siteDisarmUntilAt: createDefaultSiteDisarmUntilAt(),
    });
  }

  onCameraArmDisarmClose(): void {
    this.cameraArmDisarmAnchor.set(null);
    this.browserTabs.updateActivePageState({
      cameraArmDisarmPopover: null,
      cameraArmDisarmCameraId: null,
      siteDisarmReasonId: null,
      siteDisarmFor: 'until-next-schedule',
      siteDisarmFixedDurationMinutes: null,
      siteDisarmFixedDurationCustom: false,
      siteDisarmFixedDurationCustomDraft: '',
      siteDisarmUntilAt: null,
    });
  }

  onCameraDisarmReasonChange(reasonId: string | null): void {
    this.browserTabs.updateActivePageState({ siteDisarmReasonId: reasonId });
  }

  onCameraDisarmForChange(disarmFor: SiteDisarmForOption): void {
    const keepFixed = disarmFor === 'fixed-time';
    const page = this.browserTabs.activePageState();
    this.browserTabs.updateActivePageState({
      siteDisarmFor: disarmFor as SiteDisarmForType,
      siteDisarmFixedDurationMinutes: keepFixed ? page.siteDisarmFixedDurationMinutes : null,
      siteDisarmFixedDurationCustom: keepFixed ? page.siteDisarmFixedDurationCustom : false,
      siteDisarmFixedDurationCustomDraft: keepFixed ? page.siteDisarmFixedDurationCustomDraft : '',
      siteDisarmUntilAt:
        disarmFor === 'date-time'
          ? (page.siteDisarmUntilAt ?? createDefaultSiteDisarmUntilAt())
          : null,
    });
  }

  onCameraDisarmFixedDurationChange(minutes: SiteDisarmFixedDurationMinutes | null): void {
    this.browserTabs.updateActivePageState({ siteDisarmFixedDurationMinutes: minutes });
  }

  onCameraDisarmFixedDurationCustomChange(custom: boolean): void {
    this.browserTabs.updateActivePageState({ siteDisarmFixedDurationCustom: custom });
  }

  onCameraDisarmFixedDurationCustomDraftChange(draft: string): void {
    this.browserTabs.updateActivePageState({ siteDisarmFixedDurationCustomDraft: draft });
  }

  onCameraDisarmUntilAtChange(untilAt: string | null): void {
    this.browserTabs.updateActivePageState({ siteDisarmUntilAt: untilAt });
  }

  onCameraDisarmConfirm(event: DisarmSiteConfirm): void {
    void this.submitCameraDisarm(event);
  }

  onCameraSearch(query: string): void {
    this.browserTabs.updateActivePageState({ cameraSearchQuery: query });
  }

  onCameraSelect(cameraId: string): void {
    this.browserTabs.updateActivePageState({
      selectedCameraId: cameraId,
      liveCameraSeededEventId:
        this.eventId() || this.browserTabs.activePageState().liveCameraSeededEventId,
    });
  }

  onLiveStreamTypeChange(streamType: 'main' | 'sub'): void {
    this.browserTabs.updateActivePageState({ liveStreamType: streamType });
  }

  /** Live tiles and the popout share this toggle: the tile opens it, the popout closes it. */
  onLiveCameraPopoutToggle(cameraId: string | null): void {
    const open = this.browserTabs.activePageState().liveCameraPopoutCameraId !== null;
    this.browserTabs.updateActivePageState({
      liveCameraPopoutCameraId: open ? null : cameraId,
    });
  }

  onLiveCameraPopoutClose(): void {
    this.browserTabs.updateActivePageState({ liveCameraPopoutCameraId: null });
  }

  onPlaybackStreamTypeChange(streamType: 'main' | 'sub'): void {
    this.browserTabs.updateActivePageState({ playbackStreamType: streamType });
  }

  onPlaybackViewModeChange(mode: PlaybackViewMode): void {
    this.browserTabs.updateActivePageState({
      playbackViewMode: mode,
      playbackAnchorTimestamp:
        mode === 'clip'
          ? null
          : this.clipStartIsoForAlarm(this.browserTabs.activePageState().selectedAlarmId),
    });
  }

  onPlaybackAnchorTimestampChange(isoTimestamp: string): void {
    this.browserTabs.updateActivePageState({ playbackAnchorTimestamp: isoTimestamp });
  }

  onViewAlarmPlaybackViewModeChange(mode: PlaybackViewMode): void {
    this.browserTabs.updateActivePageState({
      viewAlarmPlaybackViewMode: mode,
      viewAlarmPlaybackAnchorTimestamp:
        mode === 'clip' ? null : this.clipStartIsoForAlarm(this.viewAlarmSelectedAlarmId()),
    });
  }

  onViewAlarmPlaybackAnchorTimestampChange(isoTimestamp: string): void {
    this.browserTabs.updateActivePageState({ viewAlarmPlaybackAnchorTimestamp: isoTimestamp });
  }

  private formatSiteTimestamp(value: string): string {
    return formatQueueTimestamp(value, this.siteClock.siteTimezone());
  }

  private formatPlaybackCameraTimestamp(
    value: string,
    timeZone?: EventCameraTimeZone,
  ): string {
    return formatPlaybackTimestamp(value, timeZone);
  }

  private resolveCamerasPlaybackTimeZone(): EventCameraTimeZone | undefined {
    const cameras = this.eventCameras();
    const selectedId = this.selectedCameraIds()[0];
    return (
      cameras.find((camera) => camera.id === selectedId)?.timeZone ?? cameras[0]?.timeZone
    );
  }

  private clipUrlForAlarm(alarmId: string | null): string | null {
    if (!alarmId) {
      return null;
    }
    return (
      (this.alarmItems()).find((item) => item.id === alarmId)?.clipUrls?.[0] ?? null
    );
  }

  private clipStartIsoForAlarm(alarmId: string | null): string | null {
    return this.clipStartIsoForAlarmFrom(this.alarmItems(), alarmId);
  }

  private clipStartIsoForAlarmFrom(
    alarms: readonly EventAlarmRow[],
    alarmId: string | null,
  ): string | null {
    if (!alarmId) {
      return null;
    }
    const alarm = alarms.find((item) => item.id === alarmId);
    const parsed = parseEventAlarmTimestamp(alarm?.timestamp);
    return parsed?.toISOString() ?? null;
  }

  private resolveCamerasPlaybackAnchorIso(): string | null {
    const eventId = this.eventId();
    const loaded =
      this.loadedAlarmRows?.eventId === eventId ? this.loadedAlarmRows.items : null;
    const alarms = loaded ?? this.alarmItems();

    const selectedIso = this.clipStartIsoForAlarmFrom(
      alarms,
      this.browserTabs.activePageState().selectedAlarmId,
    );
    if (selectedIso) {
      return selectedIso;
    }

    const earliestIso = this.clipStartIsoForAlarmFrom(
      alarms,
      findEarliestAlarm(alarms)?.id ?? null,
    );
    if (earliestIso) {
      return earliestIso;
    }

    if (loaded === null && !this.alarms.error()) {
      return null;
    }

    return this.latestSelectableDate().toISOString();
  }

  private maybeSeedCamerasPlaybackAnchor(): void {
    const state = this.browserTabs.activePageState();
    if (state.eventViewTab !== 'playback' || state.camerasPlaybackAnchorAt) {
      return;
    }

    const anchor = this.resolveCamerasPlaybackAnchorIso();
    if (!anchor) {
      return;
    }

    this.browserTabs.updateActivePageState({
      camerasPlaybackAnchorAt: anchor,
      playbackStreamStartMsByCameraId: {},
    });
  }

  onAlarmRowClick(row: EventqDataTableRow): void {
    const alarmId = String(row.id);
    this.selectedAlarmEventId = this.eventId();
    this.browserTabs.updateActivePageState({
      selectedAlarmId: alarmId,
      playbackViewMode: 'clip',
      playbackAnchorTimestamp: null,
    });
  }

  /** Page-level alarm shortcuts: modal wins while View Alarm is open. */
  onEventDetailsShortcut(actionId: string): void {
    if (this.viewAlarmOpen()) {
      this.onViewAlarmShortcut(actionId);
      return;
    }
    this.onAlarmListShortcut(actionId);
  }

  private onAlarmListShortcut(actionId: string): void {
    if (actionId === AGENT_SHORTCUT_ACTION_IDS.previous) {
      this.navigateAlarmListByOffset(-1);
      return;
    }
    if (actionId === AGENT_SHORTCUT_ACTION_IDS.next) {
      this.navigateAlarmListByOffset(1);
      return;
    }
    if (actionId === AGENT_SHORTCUT_ACTION_IDS.toggleFlag) {
      this.toggleSelectedAlarmFlag();
    }
  }

  private onViewAlarmShortcut(actionId: string): void {
    if (actionId === AGENT_SHORTCUT_ACTION_IDS.previous) {
      this.onViewAlarmPrev();
      return;
    }
    if (actionId === AGENT_SHORTCUT_ACTION_IDS.next) {
      this.onViewAlarmNext();
      return;
    }
    if (actionId === AGENT_SHORTCUT_ACTION_IDS.toggleFlag) {
      const alarmId = this.viewAlarmSelectedAlarmId();
      if (!alarmId || this.flagInFlight().has(alarmId)) {
        return;
      }
      this.onViewAlarmFlagChange(!this.viewAlarmFlagged());
    }
  }

  onAlarmClipViewed(alarmId: string): void {
    const id = alarmId.trim();
    if (!id) {
      return;
    }
    this.viewedAlarms.markViewed(this.eventId(), id);
  }

  onAlarmFullscreenRequest(currentPlaybackTimestamp: string | null = null): void {
    const pageState = this.browserTabs.activePageState();
    const alarmId = pageState.selectedAlarmId;
    if (!alarmId) {
      return;
    }

    this.browserTabs.updateActivePageState({
      viewAlarmSelectedAlarmId: alarmId,
      viewAlarmPlaybackViewMode: pageState.playbackViewMode,
      viewAlarmPlaybackAnchorTimestamp:
        currentPlaybackTimestamp ?? pageState.playbackAnchorTimestamp,
      viewAlarmOpen: true,
    });
  }

  onViewAlarmClose(): void {
    this.browserTabs.updateActivePageState({
      viewAlarmOpen: false,
      viewAlarmAiFeedbackOpen: false,
      viewAlarmAiFeedbackDetectionId: null,
      viewAlarmAiFeedbackVariant: null,
    });
  }

  onViewAlarmPrev(): void {
    this.navigateViewAlarmByOffset(-1);
  }

  onViewAlarmNext(): void {
    this.navigateViewAlarmByOffset(1);
  }

  onViewAlarmFlagChange(flagged: boolean): void {
    const alarmId = this.viewAlarmSelectedAlarmId();
    if (!alarmId) {
      return;
    }
    this.onFlagChange({ rowId: alarmId, flagged });
  }

  private navigateViewAlarmByOffset(offset: -1 | 1): void {
    const index = this.viewAlarmSelectedIndex();
    const rows = this.alarmRows();
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= rows.length) {
      return;
    }

    const nextRow = rows[nextIndex];
    const alarmId = String(nextRow.id);
    this.browserTabs.updateActivePageState({
      viewAlarmSelectedAlarmId: alarmId,
      viewAlarmPlaybackAnchorTimestamp: null,
      viewAlarmAiFeedbackOpen: false,
      viewAlarmAiFeedbackDetectionId: null,
      viewAlarmAiFeedbackVariant: null,
    });
  }

  onTalkdownSearch(query: string): void {
    this.browserTabs.updateActivePageState({ talkdownSearchQuery: query });
  }

  onActivateTalkdownOpen(groupId: string): void {
    this.browserTabs.updateActivePageState({ activateTalkdownGroupId: groupId });
  }

  onActivateTalkdownClose(): void {
    this.browserTabs.updateActivePageState({ activateTalkdownGroupId: null });
  }

  onActivateTalkdownConfirm(event: ActivateTalkdownConfirm): void {
    const groupId = this.browserTabs.activePageState().activateTalkdownGroupId;
    const talkdownId = Number.parseInt(groupId ?? '', 10);
    const group = (this.talkdowns.value()?.items ?? []).find((item) => item.id === groupId);
    const messageOptions = group?.messageOptions ?? [];
    this.onActivateTalkdownClose();

    const showFailed = (): void => {
      this.toast.show('notice', {
        title: 'Talkdown failed',
        subtitle: 'Could not activate talkdown. Try again.',
        tone: 'error',
      });
    };

    if (!Number.isFinite(talkdownId) || talkdownId <= 0) {
      showFailed();
      return;
    }

    let messageId: number | null = null;
    if (messageOptions.length > 0) {
      const selected = messageOptions.find((message) => message.id === event.optionId);
      if (!selected) {
        showFailed();
        return;
      }
      messageId = selected.messageId;
    }

    const eventId = this.eventId();
    const recordAttempt = (succeeded: boolean): void => {
      if (!eventId) {
        return;
      }
      void firstValueFrom(
        this.agentData.recordTalkdownAttempt(eventId, { talkdownId, succeeded }).pipe(
          catchError(() => of(undefined)),
        ),
      );
    };

    void (async () => {
      try {
        await firstValueFrom(
          this.agentData.activateTalkdown({
            talkdownId,
            messageId,
          }),
        );
        this.toast.show('notice', {
          title: 'Talkdown successfully activated',
          subtitle: group?.speakerName ?? `Speaker ${talkdownId}`,
          tone: 'success',
        });
        recordAttempt(true);
      } catch {
        showFailed();
        recordAttempt(false);
      }
    })();
  }

  onFilterChange(key: 'motion' | 'human' | 'vehicle' | 'gun', checked: boolean): void {
    this.browserTabs.updateActivePageState({
      alarmFilters: { ...this.browserTabs.alarmFilters(), [key]: checked },
    });
  }

  onFlagAllAlarms(): void {
    if (this.flagAllDisabled()) {
      return;
    }

    const eventId = this.eventId();
    const flags = this.alarmRows()
      .filter((row) => !row.flagged)
      .map((row) => ({ alarmId: String(row.id), isFlagged: true }));

    if (flags.length === 0) {
      return;
    }

    this.flagAllSubmitting.set(true);
    void firstValueFrom(this.agentData.flagEventAlarms(eventId, flags))
      .then(() => {
        this.alarms.reload();
      })
      .catch(() => {
        this.toast.show('notice', {
          title: 'Flag all failed',
          subtitle: 'Could not update all alarm flags. Try again.',
          tone: 'error',
        });
      })
      .finally(() => {
        this.flagAllSubmitting.set(false);
      });
  }

  onFlagChange(event: { rowId: string; flagged: boolean }): void {
    const eventId = this.eventId();
    const alarmId = event.rowId.trim();
    if (!alarmId || !eventId || this.flagInFlight().has(alarmId)) {
      return;
    }

    this.flagInFlight.update((current) => {
      const next = new Set(current);
      next.add(alarmId);
      return next;
    });

    void firstValueFrom(
      this.agentData.flagEventAlarms(eventId, [{ alarmId, isFlagged: event.flagged }]),
    )
      .then(() => {
        this.flagUpdates.update((current) => ({
          ...current,
          [alarmId]: event.flagged,
        }));
      })
      .catch(() => {
        this.toast.show('notice', {
          title: 'Flag failed',
          subtitle: 'Could not update the alarm flag. Try again.',
          tone: 'error',
        });
      })
      .finally(() => {
        this.flagInFlight.update((current) => {
          const next = new Set(current);
          next.delete(alarmId);
          return next;
        });
      });
  }

  private navigateAlarmListByOffset(offset: -1 | 1): void {
    const rows = this.alarmRows();
    if (rows.length === 0) {
      return;
    }

    const selectedId = this.browserTabs.activePageState().selectedAlarmId;
    const currentIndex = selectedId
      ? rows.findIndex((row) => String(row.id) === selectedId)
      : -1;

    let nextIndex: number;
    if (currentIndex < 0) {
      nextIndex = offset === 1 ? 0 : rows.length - 1;
    } else {
      nextIndex = currentIndex + offset;
      if (nextIndex < 0 || nextIndex >= rows.length) {
        return;
      }
    }

    const nextRow = rows[nextIndex];
    this.onAlarmRowClick(nextRow);
    this.alarmsTable()?.focusRow(String(nextRow.id));
  }

  private toggleSelectedAlarmFlag(): void {
    const alarmId = this.browserTabs.activePageState().selectedAlarmId;
    if (!alarmId || this.flagInFlight().has(alarmId)) {
      return;
    }
    this.onFlagChange({ rowId: alarmId, flagged: !this.isAlarmFlagged(alarmId) });
  }

  onAlarmSort(sort: EventqSortState): void {
    this.browserTabs.updateActivePageState({ alarmSort: sort });
  }

  onRaiseToResolve(): void {
    this.openEventViewPopover('raise-to-resolve');
  }

  onShowDetailsInResolve(): void {
    const eventId = this.eventId();
    if (!eventId) {
      return;
    }
    const url = siblingAppHref(
      environment.resolveAppBaseUrl,
      `/t1/${tabEventRoutePath(eventId)}`,
      '/resolve',
    );
    this.siblingSync.scheduleSync(() => {
      openSiblingAppWindow(url, RESOLVE_SYNC_WINDOW_NAME, {
        popout: environment.features.enableTabPopout,
      });
    });
  }

  onParkEvent(): void {
    this.openEventViewPopover('park-event');
  }

  onCloseEvent(): void {
    this.openEventViewPopover('close-event');
  }

  onPopoverClose(): void {
    this.popoverSubmitAttempted.set(false);
    this.browserTabs.updateActivePageState({
      eventViewPopover: null,
      eventViewPopoverSearchQuery: '',
      eventViewPopoverPriorities: [],
      eventViewPopoverSelectedOutcomeId: null,
      eventViewPopoverClosingDetails: '',
      eventViewPopoverParkDurationMinutes: 0,
    });
  }

  onPopoverSearchChange(query: string): void {
    this.browserTabs.updateActivePageState({ eventViewPopoverSearchQuery: query });
  }

  onPopoverPrioritiesChange(priorities: EventOutcomePriority[]): void {
    this.browserTabs.updateActivePageState({ eventViewPopoverPriorities: priorities });
  }

  onPopoverOutcomeSelect(outcomeId: string): void {
    this.browserTabs.updateActivePageState({ eventViewPopoverSelectedOutcomeId: outcomeId });
  }

  onPopoverClosingDetailsChange(details: string): void {
    this.browserTabs.updateActivePageState({ eventViewPopoverClosingDetails: details });
  }

  onParkDurationChange(durationMinutes: ParkEventDurationMinutes): void {
    this.browserTabs.updateActivePageState({
      eventViewPopoverParkDurationMinutes: durationMinutes,
    });
  }

  onCamerasPlaybackAnchorApplied(date: Date): void {
    this.popoverSubmitAttempted.set(false);
    const instant = fromNvrWallClockDate(date, this.camerasPlaybackTimeZone());
    this.browserTabs.updateActivePageState({
      camerasPlaybackAnchorAt: instant.toISOString(),
      // A new global time wins over every per-tile calendar time already applied.
      playbackStreamStartMsByCameraId: {},
    });
  }

  onRaiseConfirm(payload: EventOutcomeConfirm): void {
    this.submitPopoverAction('raise-to-resolve', payload);
  }

  onParkConfirm(payload: ParkEventConfirm): void {
    const eventId = this.eventId();
    if (!eventId) {
      return;
    }

    this.popoverSubmitAttempted.set(false);
    this.browserTabs.updateActivePageState({
      eventViewPopover: null,
      eventViewPopoverParkDurationMinutes: 0,
      pendingAction: {
        type: 'park-event',
        eventId,
        eventLocation: this.detail.value()?.location,
        durationMinutes: payload.durationMinutes,
      },
    });

    void this.tabNavigation.navigateWithinActiveTab('load', 'park-event');
  }

  onCloseConfirm(payload: EventOutcomeConfirm): void {
    if (this.browserTabs.pendingAction()?.type === 'close-event') {
      return;
    }

    const closingDetails = payload.closingDetails ?? '';
    if (closingDetails.length > CLOSING_DETAILS_MAX_LENGTH) {
      return;
    }

    if (!closingDetails.trim()) {
      this.popoverSubmitAttempted.set(true);
      return;
    }

    this.submitPopoverAction('close-event', payload);
  }

  private submitPopoverAction(type: EventViewPopoverType, payload: EventOutcomeConfirm): void {
    const eventId = this.eventId();

    if (!type || type === 'park-event' || !eventId || !payload.outcomeId) {
      return;
    }

    this.popoverSubmitAttempted.set(false);
    this.browserTabs.updateActivePageState({
      eventViewPopover: null,
      eventViewPopoverSearchQuery: '',
      eventViewPopoverPriorities: [],
      eventViewPopoverSelectedOutcomeId: null,
      eventViewPopoverClosingDetails: '',
      eventViewPopoverParkDurationMinutes: 0,
      pendingAction: {
        type,
        eventId,
        outcomeId: payload.outcomeId,
        outcomeLabel: this.resolveOutcomeLabel(type, payload.outcomeId),
        eventLocation: this.detail.value()?.location,
        closingDetails: payload.closingDetails,
      },
    });

    const loadingSegment = type === 'raise-to-resolve' ? 'raise-to-resolve' : 'close-event';

    void this.tabNavigation.navigateWithinActiveTab('load', loadingSegment);
  }

  private resolveOutcomeLabel(type: EventViewPopoverType, outcomeId: string): string {
    const groups =
      type === 'raise-to-resolve'
        ? (this.raiseOutcomes.value()?.groups ?? [])
        : (this.closeOutcomes.value()?.groups ?? []);

    for (const group of groups) {
      const match = group.items.find((item) => item.id === outcomeId);
      if (match) {
        return match.label;
      }
    }

    return outcomeId;
  }

  private openEventViewPopover(type: EventViewPopoverType): void {
    if (!type) {
      return;
    }

    this.popoverSubmitAttempted.set(false);
    this.browserTabs.updateActivePageState({
      eventViewPopover: type,
      eventViewPopoverPriorities: [],
      eventViewPopoverSelectedOutcomeId: null,
      eventViewPopoverParkDurationMinutes: 0,
    });
  }

  /** Interval poll: always hit Dealer, then overwrite the cameras cache so `reload()` is not a no-op. */
  private async refreshCamerasFromDealer(): Promise<void> {
    const eventId = this.eventId();
    const siteId = this.siteId();
    if (!eventId || siteId == null || siteId <= 0) {
      return;
    }

    try {
      const result = await firstValueFrom(this.agentData.getEventCameras(siteId));
      this.eventCache.write('cameras', eventId, result, siteId);
      this.cameras.reload();
    } catch {
      this.toast.show('notice', {
        title: 'Cameras',
        subtitle: 'Unable to refresh cameras.',
        tone: 'error',
      });
    }
  }

  private async submitCameraDisarm(event: DisarmSiteConfirm): Promise<void> {
    const camera = this.selectedCameraArmDisarm();
    if (!camera || !this.eventId() || this.cameraArmDisarmSubmitting()) {
      return;
    }

    this.cameraArmDisarmSubmitting.set(true);
    try {
      await firstValueFrom(
        this.agentData.disarmCamera(camera.id, {
          reasonId: event.reasonId,
          disarmFor: event.disarmFor,
          durationMinutes: event.durationMinutes,
          untilAt: event.untilAt,
        }),
      );
      this.toast.show('site-disarmed', {
        title: 'Camera Disarmed',
        subtitle: `${camera.name} disarm was submitted. It can take up to a minute to show as disarmed.`,
        iconSrc: '/assets/events/icons/toast/site-disarmed.svg',
      });
      this.onCameraArmDisarmClose();
      this.eventCache.invalidate('cameras', this.eventId());
      this.cameras.reload();
    } catch {
      this.toast.show('notice', {
        title: 'Disarm failed',
        subtitle: `Unable to disarm ${camera.name}.`,
        tone: 'error',
      });
    } finally {
      this.cameraArmDisarmSubmitting.set(false);
    }
  }

  private armDisarmRowMatches(row: EventqDataTableRow, query: string): boolean {
    return ['name', 'disarmingNotes', 'disarmedAt', 'disarmedBy'].some((key) =>
      String(row[key] ?? '')
        .toLowerCase()
        .includes(query),
    );
  }

  private toArmDisarmRow(camera: EventCameraItem): EventqDataTableRow {
    return {
      id: camera.id,
      status: camera.status === 'disarmed' ? 'disarmed' : 'armed',
      name: camera.name,
      disarmingNotes: this.formatDisarmingNotes(camera),
      disarmedAt: this.formatArmTimestamp(camera.disarmedAt),
      disarmedBy: camera.disarmedBy ?? '',
      disarmedAtSort: camera.disarmedAt ?? '',
    };
  }

  private formatDisarmingNotes(camera: EventCameraItem): string {
    const reason = camera.disarmingNotes?.trim() ?? '';
    const rearmingAt = this.formatArmTimestamp(camera.disarmedEndAt);
    if (reason && rearmingAt) {
      return `Disarming Reason: ${reason}, Rearming at ${rearmingAt}`;
    }
    if (reason) {
      return `Disarming Reason: ${reason}`;
    }
    if (rearmingAt) {
      return `Rearming at ${rearmingAt}`;
    }
    return '';
  }

  private formatArmTimestamp(iso: string | null | undefined): string {
    const trimmed = iso?.trim();
    if (!trimmed) {
      return '';
    }

    const instant = ISO_WITHOUT_OFFSET.test(trimmed) ? `${trimmed}Z` : trimmed;
    const parsed = new Date(instant);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    // en-US hourCycle h23 prints local midnight as 24:00.
    return formatQueueTimestamp(parsed.toISOString(), this.disarmDisplayTimeZone()).replace(
      ' 24:',
      ' 00:',
    );
  }

  /** Site IANA zone, or UTC when the site zone is missing or not a valid name. */
  disarmDisplayTimeZone(): string {
    const requested = this.siteClock.siteTimezone()?.trim();
    if (!requested) {
      return 'UTC';
    }

    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: requested }).format(new Date());
      return requested;
    } catch {
      return 'UTC';
    }
  }

  private sortArmDisarmRows(
    rows: EventqDataTableRow[],
    sort: EventqSortState | null,
  ): EventqDataTableRow[] {
    if (!sort) {
      return rows;
    }

    const direction = sort.direction === 'asc' ? 1 : -1;
    const column = sort.column;

    return [...rows].sort((a, b) => {
      if (column === 'disarmedAt') {
        return (
          String(a['disarmedAtSort'] ?? '').localeCompare(String(b['disarmedAtSort'] ?? '')) *
          direction
        );
      }
      return String(a['name'] ?? '').localeCompare(String(b['name'] ?? '')) * direction;
    });
  }

  private isAlarmFlagged(alarmId: string): boolean {
    const updated = this.flagUpdates()[alarmId];
    if (updated !== undefined) {
      return updated;
    }
    return this.alarmItems().find((alarm) => alarm.id === alarmId)?.flagged ?? false;
  }

  private matchesFilter(
    alarm: EventAlarmRow,
    filters: { motion: boolean; human: boolean; vehicle: boolean; gun: boolean },
  ): boolean {
    if (alarm.indicator === 'none') {
      return true;
    }
    return filters[alarm.indicator as keyof typeof filters] ?? true;
  }

  private sortAlarmRows<T extends Record<string, unknown>>(
    rows: T[],
    sort: EventqSortState | null,
  ): T[] {
    if (!sort) {
      return rows;
    }

    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => {
      const leftValue = left[sort.column];
      const rightValue = right[sort.column];

      if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
        return (Number(leftValue) - Number(rightValue)) * direction;
      }

      if (sort.column === 'timestamp') {
        return (
          compareEventAlarmTimestamps(
            leftValue === undefined || leftValue === null ? null : String(leftValue),
            rightValue === undefined || rightValue === null ? null : String(rightValue),
          ) * direction
        );
      }

      const leftText = leftValue === undefined || leftValue === null ? '' : String(leftValue);
      const rightText = rightValue === undefined || rightValue === null ? '' : String(rightValue);
      return leftText.localeCompare(rightText) * direction;
    });
  }
}
